/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useState, useEffect} from 'react'
import {debounce} from 'lodash'
import {Helmet} from 'react-helmet'
import {collection, doc, onSnapshot} from 'firebase/firestore'
import {ListLoading} from 'app/components/ListLoading'
import {useAuth} from 'app/modules/auth'
import db from 'services/firebase'
import {KTCard,  toAbsoluteUrl} from '_metronic/helpers'
import {AccessPageTitle} from '_metronic/layout/core/AccessPageData'
import {SystemBadges} from '../components/SystemBadge'
import {ToastContainer} from 'react-toastify'
import FormatTimestamp from 'app/components/FormatTimestamp'
import StatusBadge from 'app/components/StatusBadge'
import ReviewFilterBar from '../components/Filters/ReviewFilterBar'
import { Link, useLocation } from 'react-router-dom'
import OwnerDisplay from 'app/components/OwnerDisplay'
import { showErrorToast, showSuccessToast } from 'app/components/ShowToast'
import { DisplayReview, LocationState, ReviewData } from '../utils/PagesUtils'

const ReviewPage: React.FC = () => {
  const {currentUser} = useAuth()
  const [reviews, setReviews] = useState<DisplayReview[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredItems, setFilteredItems] = useState<DisplayReview[]>([])
  const [searchLength, setSearchLength] = useState(false)
  const [showEmptyState, setShowEmptyState] = useState<boolean>(false)
  const [sortBy, setSortBy] = useState('reviewName')
  const [sortDirection, setSortDirection] = useState('asc')
  const [owners, setOwners] = useState<any[]>([])
  const [selectReviewer, setSelectReviewer] = useState('')
  const [selectStatus, setSelectStatus] = useState('')
  const [showAllSystems, setShowAllSystems] = useState(false)
  const location = useLocation();
  const {toast} = (location.state as LocationState) || {}

  useEffect(() => {
    if (toast) {
      const { message, type } = toast;
      if (type === 'success') {
        showSuccessToast({ message });
      } else if (type === 'error') {
        showErrorToast({ message });
      }
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [toast]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowEmptyState(true)
    }, 2000)
    return () => clearTimeout(timeoutId)
  }, [])

  useEffect(() => {
    getReviews()
  }, [currentUser])

  useEffect(() => {
    setFilteredItems(reviews)
  }, [reviews])

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    if (!tenantId) return

    const tenantRef = doc(db, 'tenants', tenantId)
    const policyRef = collection(tenantRef, 'employees')

    const unsubscribe = onSnapshot(policyRef, (snapshot) => {
      const risksData = snapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
          end_date: doc.data().end_date,
          role: doc.data().role,
        }))
        .filter((data) => !data.end_date)
      setOwners(risksData.filter((item) => item.role !== 'Auditor'))
    })

    return () => unsubscribe()
  }, [currentUser])

  const getReviews = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!tenantId) return

      const tenantRef = doc(db, 'tenants', tenantId)
      const reviewsRef = collection(tenantRef, 'reviews')

      const unsubscribe = onSnapshot(reviewsRef, (snapshot) => {
        const allReviews: DisplayReview[] = snapshot.docs.map((doc) => {
          const reviewData = doc.data() as ReviewData
          const systems = reviewData.systems || {}

          const systemNames = Object.values(systems)
            .map((s) => s.name)
            .join(', ')
          const systemLogos = Object.values(systems)
            .map((s) => s.icon)
            .join(', ')
          const domains = Object.values(systems)
            .map((s) => s.domain)
            .join(', ')
          const riskScores = Object.values(systems)
            .map((s) => s.riskScore)
            .join(', ')

          return {
            id: doc.id,
            reviewName: reviewData.reviewName,
            systemNames,
            systemLogos,
            domains,
            dueDate: reviewData.dueDate,
            reviewer: reviewData.reviewer,
            riskScores,
            integrationStatuses: reviewData.status,
            startDate: reviewData.startDate,
            createdAt: reviewData.createdAt,
            updatedAt: reviewData.updatedAt,
            completedAt: reviewData.completedAt,
          }
        })

        setReviews(allReviews)
        setShowEmptyState(false)
      })

      return () => unsubscribe()
    } catch (error) {
      console.error('Error getting reviews:', error)
      setShowEmptyState(false)
    }
  }

  const debouncedHandleInputChange = debounce(() => {
    if (reviews.length > 0) {
      if (!searchTerm && !selectReviewer && !selectStatus) {
        setFilteredItems(reviews)
        setSearchLength(false)
      } else {
        const filtered = reviews.filter((review) => {
          const fields = [
            review.reviewName,
            review.systemNames,
            review.domains,
            review.reviewer,
            review.riskScores,
            review.integrationStatuses,
          ].map((field) => (typeof field === 'string' ? field.toLowerCase() : ''))

          const matchesSearch = searchTerm
            ? fields.some((field) => field.includes(searchTerm.toLowerCase()))
            : true
          const matchesReviewer = selectReviewer ? review.reviewer.name === selectReviewer : true
          const matchesStatus = selectStatus
            ? review.integrationStatuses.toLowerCase() === selectStatus.toLowerCase()
            : true

          return matchesSearch && matchesReviewer && matchesStatus
        })
        setFilteredItems(filtered)
        setSearchLength(filtered.length === 0)
      }
    }
  }, 300)

  useEffect(() => {
    debouncedHandleInputChange()
  }, [searchTerm, selectReviewer, selectStatus, reviews])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = e.target.value
    setSearchTerm(searchTerm)
  }

  const handleSort = (column: string) => {
    const newSortDirection = column === sortBy && sortDirection === 'asc' ? 'desc' : 'asc'
    setSortBy(column)
    setSortDirection(newSortDirection)
  
    const sortedItems = [...filteredItems].sort((a, b) => {
      if (
        column === 'reviewName' ||
        column === 'systemNames' ||
        column === 'domains' ||
        column === 'riskScores' ||
        column === 'integrationStatuses'
      ) {
        const valueA = a[column] || ''
        const valueB = b[column] || ''
        return newSortDirection === 'asc'
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA)
      } else if (column === 'reviewer') {
        const valueA = a.reviewer?.name || ''
        const valueB = b.reviewer?.name || ''
        return newSortDirection === 'asc'
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA)
      } else if (column === 'createdAt' || column === 'updatedAt') {
        const dateA = a[column] ? new Date(a[column].seconds * 1000).getTime() : 0
        const dateB = b[column] ? new Date(b[column].seconds * 1000).getTime() : 0
        return newSortDirection === 'asc' ? dateA - dateB : dateB - dateA
      }
      return 0
    })
  
    setFilteredItems(sortedItems)
  }

  return (
    <>
      <Helmet>
        <title>SecureSlate | User Access</title>
      </Helmet>
      <AccessPageTitle
        icon='user-access.svg'
        title='User Review'
        desc='Review access on your critical systems and applications'
      >
        User Access
      </AccessPageTitle>
      <KTCard>
        <ReviewFilterBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          owners={owners}
          selectedPlatform={selectReviewer}
          setSelectedPlatform={setSelectReviewer}
          selectedStatus={selectStatus}
          setSelectedStatus={setSelectStatus}
          handleInputChange={handleInputChange}
          type='review'
        />
        <div className='card-body py-3'>
          <div className='table-responsive' style={{minHeight: '60vh', overflowY: 'auto'}}>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-bold' style={{color: '#5A5A5A'}}>
                  <th className='min-w-150px' onClick={() => handleSort('reviewName')}>
                    REVIEW NAME{' '}
                    {sortBy === 'reviewName' && (
                      <span
                        className={`ms-1 icon-xxs text-muted ${
                          sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                        }`}
                      />
                    )}
                  </th>
                  <th className='min-w-100px' onClick={() => handleSort('reviewer')}>
                    REVIEWER{' '}
                    {sortBy === 'reviewer' && (
                      <span
                        className={`ms-1 icon-xxs text-muted ${
                          sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                        }`}
                      />
                    )}
                  </th>
                  <th className='min-w-100px' onClick={() => handleSort('integrationStatuses')}>
                    STATUS{' '}
                    {sortBy === 'integrationStatuses' && (
                      <span
                        className={`ms-1 icon-xxs text-muted ${
                          sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                        }`}
                      />
                    )}
                  </th>
                  <th className='min-w-150px' onClick={() => handleSort('systemNames')}>
                    REVIEWING{' '}
                    {sortBy === 'systemNames' && (
                      <span
                        className={`ms-1 icon-xxs text-muted ${
                          sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                        }`}
                      />
                    )}
                  </th>
                  <th className='min-w-100px' onClick={() => handleSort('createdAt')}>
                    STARTED ON{' '}
                    {sortBy === 'createdAt' && (
                      <span
                        className={`ms-1 icon-xxs text-muted ${
                          sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                        }`}
                      />
                    )}
                  </th>
                  <th className='min-w-100px' onClick={() => handleSort('updatedAt')}>
                    COMPLETED ON{' '}
                    {sortBy === 'updatedAt' && (
                      <span
                        className={`ms-1 icon-xxs text-muted ${
                          sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                        }`}
                      />
                    )}
                  </th>
                  <th className='min-w-100px'>Due Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems && filteredItems.length > 0 ? (
                  filteredItems.map((review) => (
                    <tr key={review.id}>
                      <td>
                        <Link
                          to={`/access/review/${review.id}`}
                          className='fw-semibold d-block fs-6 text-dark text-hover-primary'
                        >
                          {review.reviewName}
                        </Link>
                      </td>
                      <td>
                        <span className='text-dark fw-semibold d-block fs-6'>
                         <OwnerDisplay owner={review.reviewer.name} />
                        </span>
                      </td>
                      <td>
                        <span className='text-dark fw-semibold d-block fs-6'>
                            <StatusBadge status={review.integrationStatuses as "Active" | "Inactive" | "Deactivated" | "Pending" | "Due" | "Completed" | "In review"} />
                        </span>
                      </td>
                      <td>
                        <SystemBadges
                          systemNames={review?.systemNames.split(', ')}
                          systemLogos={review?.systemLogos.split(', ')}
                          showAll={showAllSystems}
                          toggleShow={() => setShowAllSystems((prev) => !prev)}
                        />
                      </td>
                      <td>
                        <span className='fw-semibold fs-6'>
                          <FormatTimestamp timestamp={review.startDate} />
                        </span>
                      </td>
                      <td>
                        <span className='fw-semibold fs-6'>
                          <FormatTimestamp timestamp={review.completedAt} />
                        </span>
                      </td>
                      <td>
                        <span className='fw-semibold fs-6'>
                          <FormatTimestamp timestamp={review.dueDate} />
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr style={{height: '100%'}}>
                    {searchLength ? (
                      <td colSpan={7}>
                        <div
                          className='d-flex justify-content-center align-items-center text-center'
                          style={{height: '100%'}}
                        >
                          <div className='m-20'>
                            <img src={toAbsoluteUrl('/media/buttons/emptystate.svg')} alt='' />
                            <div className='m-5 fs-5'>
                              <p style={{fontWeight: 'bold'}}>No Reviews Found</p>
                            </div>
                            <p>
                              We couldn't find any reviews that match your search. Maybe try a
                              different search!
                            </p>
                          </div>
                        </div>
                      </td>
                    ) : (
                      <td colSpan={7}>
                        <div
                          className='d-flex justify-content-center align-items-center text-center'
                          style={{height: '100%'}}
                        >
                          {!showEmptyState ? (
                            <ListLoading />
                          ) : (
                            <div className='m-20'>
                              <img src={toAbsoluteUrl('/media/buttons/emptystate.svg')} alt='' />
                              <div className='m-5 fs-5'>
                                <p style={{fontWeight: '600'}}>No Reviews Added</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </KTCard>
      <ToastContainer />
    </>
  )
}

export {ReviewPage}
